import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSound } from '../contexts/SoundContext';

const NavigationMenu = () => {
  const { t, language, changeLanguage, availableLanguages } = useLanguage();
  const { theme, changeTheme, availableThemes } = useTheme();
  const { playNavigationClick, playSelectOptionOpen } = useSound();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const location = useLocation();

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('.navigation-menu')) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden'; // Prevent body scroll when menu is open
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // Close dropdowns when clicking outside (for mobile menu)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isThemeOpen && !event.target.closest('.theme-selector-mobile')) {
        setIsThemeOpen(false);
      }
      if (isLanguageOpen && !event.target.closest('.language-selector-mobile')) {
        setIsLanguageOpen(false);
      }
    };

    if (isThemeOpen || isLanguageOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isThemeOpen, isLanguageOpen]);

  const currentLanguage = availableLanguages.find(lang => lang.code === language);
  const currentTheme = availableThemes.find(t => t.code === theme);

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setIsLanguageOpen(false);
  };

  const handleThemeChange = (themeCode) => {
    changeTheme(themeCode);
    setIsThemeOpen(false);
  };

  const menuItems = [
    { path: '/', label: t('footer.calculator') || 'Calculator', icon: '🤖' },
    { path: '/pilot-upgrade-calculator', label: t('footer.pilotCalculator') || 'Pilot Calculator', icon: '👤' },
    { path: '/mod-calculator', label: t('footer.modCalculator') || 'Mod Calculator', icon: '🧩' },
    { path: '/my-stats', label: t('footer.statistics') || 'Statistics', icon: '📊' },
    { path: '/legendary-skins', label: t('footer.legendarySkins') || 'Legendary Skins', icon: '⭐' },
    { path: '/maps', label: 'Maps', icon: '🗺️' },
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === path || location.pathname.startsWith(path);
  };

  return (
    <nav className="navigation-menu relative">
      {/* Hamburger Button (Mobile Only) */}
      <button
        onClick={() => {
          playSelectOptionOpen();
          setIsMenuOpen(!isMenuOpen);
        }}

        className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg bg-white/80 dark:bg-[#1A1A1A]/90 backdrop-blur-sm border border-indigo-200 dark:border-[#333333] hover:border-indigo-300 dark:hover:border-[#FF8A65] hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-orange-glow focus:ring-offset-2 dark:focus:ring-offset-[#121212]"
        aria-label="Toggle menu"
        aria-expanded={isMenuOpen}
      >
        {isMenuOpen ? (
          <svg className="w-6 h-6 text-gray-700 dark:text-[#FF5722]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-6 h-6 text-gray-700 dark:text-[#FF9800]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        )}
      </button>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setIsMenuOpen(false)}
          />
          
          {/* Menu Panel */}
          <div className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white dark:bg-[#121212] shadow-2xl z-50 md:hidden transform transition-transform duration-300 ease-in-out overflow-y-auto border-l border-blue-100 dark:border-[#2A2A2A]">
            <div className="p-4">
              {/* Close Button */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-800 dark:text-[#FF5722]">
                  {t('header.menu') || 'Menu'}
                </h2>
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1E1E1E] transition-colors"
                  aria-label="Close menu"
                >
                  <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Navigation Links */}
              <div className="space-y-1 mb-6">
                {menuItems.map((item) => {
                  const active = isActive(item.path);
                  const Component = item.external ? 'a' : Link;
                  const props = item.external 
                    ? { href: item.path }
                    : { to: item.path };

                  return (
                    <Component
                      key={item.path}
                      {...props}
                      onClick={() => {
                        playNavigationClick();
                        setIsMenuOpen(false);
                        // Track navigation click
                        if (typeof gtag !== 'undefined') {
                          gtag('event', 'navigation_click', {
                            'destination': item.path,
                            'link_text': item.label,
                            'menu_type': 'mobile'
                          });
                        }
                      }}
              
                      className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-200 ${
                        active
                          ? 'bg-indigo-50 dark:bg-[#1E1E1E] text-indigo-700 dark:text-[#FF9800] font-medium'
                          : 'text-gray-700 dark:text-[#B0B0B0] hover:bg-gray-100 dark:hover:bg-[#1E1E1E]'
                      }`}
                    >
                      <span className="text-xl">{item.icon}</span>
                      <span>{item.label}</span>
                      {active && (
                        <svg className="w-4 h-4 ml-auto text-indigo-600 dark:text-[#FF5722]" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </Component>
                  );
                })}
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200 dark:border-[#444444] my-4" />

              {/* Theme Selector */}
              <div className="mb-4 theme-selector-mobile">
                <label className="block text-sm font-medium text-gray-700 dark:text-[#FF7043] mb-2">
                  {t('header.theme') || 'Theme'}
                </label>
                <div className="relative">
                  <button
                    onClick={() => {
                      if (!isThemeOpen) {
                        playSelectOptionOpen();
                      }
                      setIsThemeOpen(!isThemeOpen);
                    }}
                    className="w-full flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-[#1E1E1E] border border-gray-200 dark:border-[#333333] rounded-lg hover:border-indigo-300 dark:hover:border-[#FF8A65] transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{currentTheme?.icon}</span>
                      <span className="text-sm font-medium text-gray-700 dark:text-[#FFFFFF]">{currentTheme?.name}</span>
                    </div>
                    <svg 
                      className={`w-4 h-4 text-gray-500 dark:text-[#FF9800] transition-transform duration-200 ${isThemeOpen ? 'rotate-180' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isThemeOpen && (
                    <div 
                      className="absolute left-0 right-0 mt-2 bg-white dark:bg-[#1A1A1A] border border-gray-200 dark:border-[#333333] rounded-lg shadow-lg z-[60]"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <div className="py-1">
                        {availableThemes.map((themeOption) => (
                          <button
                            key={themeOption.code}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              playNavigationClick();
                              handleThemeChange(themeOption.code);
                              // Track theme change
                              if (typeof gtag !== 'undefined') {
                                gtag('event', 'preference_changed', {
                                  'preference_type': 'theme',
                                  'preference_value': themeOption.code
                                });
                              }
                            }}
                    
                            className={`w-full flex items-center space-x-3 px-4 py-2 text-sm hover:bg-indigo-50 dark:hover:bg-[#1E1E1E] transition-colors duration-200 ${
                              theme === themeOption.code 
                                ? 'bg-indigo-50 dark:bg-[#1E1E1E] text-indigo-700 dark:text-[#FF9800]' 
                                : 'text-gray-700 dark:text-[#B0B0B0]'
                            }`}
                          >
                            <span className="text-lg">{themeOption.icon}</span>
                            <div className="flex flex-col items-start">
                              <span className="font-medium">{themeOption.name}</span>
                              <span className="text-xs text-gray-500 dark:text-[#8A8A8A]">{themeOption.description}</span>
                            </div>
                            {theme === themeOption.code && (
                              <svg className="w-4 h-4 text-indigo-600 dark:text-[#FF5722] ml-auto" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Language Selector */}
              <div className="mb-4 language-selector-mobile">
                <label className="block text-sm font-medium text-gray-700 dark:text-[#FF7043] mb-2">
                  {t('header.language') || 'Language'}
                </label>
                <div className="relative">
                  <button
                    onClick={() => {
                      if (!isLanguageOpen) {
                        playSelectOptionOpen();
                      }
                      setIsLanguageOpen(!isLanguageOpen);
                    }}
                    className="w-full flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-[#1E1E1E] border border-gray-200 dark:border-[#333333] rounded-lg hover:border-indigo-300 dark:hover:border-[#FF8A65] transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{currentLanguage?.flag}</span>
                      <span className="text-sm font-medium text-gray-700 dark:text-[#FFFFFF]">{currentLanguage?.name}</span>
                    </div>
                    <svg 
                      className={`w-4 h-4 text-gray-500 dark:text-[#FF9800] transition-transform duration-200 ${isLanguageOpen ? 'rotate-180' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isLanguageOpen && (
                    <div 
                      className="absolute left-0 right-0 mt-2 bg-white dark:bg-[#1A1A1A] border border-gray-200 dark:border-[#333333] rounded-lg shadow-lg z-[60] max-h-64 overflow-y-auto"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <div className="py-1">
                        {availableLanguages.map((lang) => (
                          <button
                            key={lang.code}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              playNavigationClick();
                              handleLanguageChange(lang.code);
                              // Track language change
                              if (typeof gtag !== 'undefined') {
                                gtag('event', 'preference_changed', {
                                  'preference_type': 'language',
                                  'preference_value': lang.code
                                });
                              }
                            }}
                    
                            className={`w-full flex items-center space-x-3 px-4 py-2 text-sm hover:bg-indigo-50 dark:hover:bg-[#1E1E1E] transition-colors duration-200 ${
                              language === lang.code ? 'bg-indigo-50 dark:bg-[#1E1E1E] text-indigo-700 dark:text-[#FF9800]' : 'text-gray-700 dark:text-[#B0B0B0]'
                            }`}
                          >
                            <span className="text-lg">{lang.flag}</span>
                            <span className="font-medium">
                              {lang.name}
                            </span>
                            {language === lang.code && (
                              <svg className="w-4 h-4 text-indigo-600 dark:text-[#FF5722] ml-auto" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Desktop Menu */}
      <div className="hidden md:flex items-center space-x-2">
        {/* Navigation Links */}
        <div className="flex items-center space-x-1">
          {menuItems.map((item) => {
            const active = isActive(item.path);
            const Component = item.external ? 'a' : Link;
            const props = item.external 
              ? { href: item.path }
              : { to: item.path };

            return (
              <Component
                key={item.path}
                {...props}
                onClick={() => {
                  playNavigationClick();
                  // Track navigation click
                  if (typeof gtag !== 'undefined') {
                    gtag('event', 'navigation_click', {
                      'destination': item.path,
                      'link_text': item.label,
                      'menu_type': 'desktop'
                    });
                  }
                }}
        
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  active
                    ? 'bg-indigo-50 dark:bg-[#1E1E1E] text-indigo-700 dark:text-[#FF9800]'
                    : 'text-gray-700 dark:text-[#B0B0B0] hover:bg-gray-100 dark:hover:bg-[#1E1E1E]'
                }`}
              >
                <span>{item.icon}</span>
                <span>{item.label}</span>
              </Component>
            );
          })}
        </div>

        {/* Divider */}
        <div className="h-6 w-px bg-gray-300 dark:bg-[#2A2A2A] mx-2" />

        {/* Theme Selector */}
        <div className="relative">
          <button
            onClick={() => {
              if (!isThemeOpen) {
                playSelectOptionOpen();
              }
              setIsThemeOpen(!isThemeOpen);
            }}
            className="flex items-center space-x-2 px-3 py-2 bg-white/80 dark:bg-[#1A1A1A]/80 backdrop-blur-sm border border-indigo-200 dark:border-[#333333] rounded-lg hover:border-indigo-300 dark:hover:border-[#FF8A65] hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-orange-glow focus:ring-offset-2 dark:focus:ring-offset-[#121212]"
            aria-label="Select theme"
          >
            <span className="text-lg">{currentTheme?.icon}</span>
            <span className="text-sm font-medium text-gray-700 dark:text-[#FFFFFF]">{currentTheme?.name}</span>
            <svg 
              className={`w-4 h-4 text-gray-500 dark:text-[#FF9800] transition-transform duration-200 ${isThemeOpen ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isThemeOpen && (
            <>
              <div 
                className="fixed inset-0 z-40" 
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) {
                    setIsThemeOpen(false);
                  }
                }}
              />
              <div 
                className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#1A1A1A] border border-indigo-200 dark:border-[#333333] rounded-lg shadow-lg z-50"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div className="py-1">
                  {availableThemes.map((themeOption) => (
                    <button
                      key={themeOption.code}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        playNavigationClick();
                        handleThemeChange(themeOption.code);
                        // Track theme change
                        if (typeof gtag !== 'undefined') {
                          gtag('event', 'preference_changed', {
                            'preference_type': 'theme',
                            'preference_value': themeOption.code
                          });
                        }
                      }}
              
                      className={`w-full flex items-center space-x-3 px-4 py-2 text-sm hover:bg-indigo-50 dark:hover:bg-[#1E1E1E] transition-colors duration-200 ${
                        theme === themeOption.code 
                          ? 'bg-indigo-50 dark:bg-[#1E1E1E] text-indigo-700 dark:text-[#FF9800]' 
                          : 'text-gray-700 dark:text-[#B0B0B0]'
                      }`}
                    >
                      <span className="text-lg">{themeOption.icon}</span>
                      <div className="flex flex-col items-start">
                        <span className="font-medium">{themeOption.name}</span>
                        <span className="text-xs text-gray-500 dark:text-[#8A8A8A]">{themeOption.description}</span>
                      </div>
                      {theme === themeOption.code && (
                        <svg className="w-4 h-4 text-indigo-600 dark:text-[#FF5722] ml-auto" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Language Selector */}
        <div className="relative">
          <button
            onClick={() => {
              if (!isLanguageOpen) {
                playSelectOptionOpen();
              }
              setIsLanguageOpen(!isLanguageOpen);
            }}
            className="flex items-center space-x-2 px-3 py-2 bg-white/80 dark:bg-[#1A1A1A]/80 backdrop-blur-sm border border-indigo-200 dark:border-[#333333] rounded-lg hover:border-indigo-300 dark:hover:border-[#FF8A65] hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-orange-glow focus:ring-offset-2 dark:focus:ring-offset-[#121212]"
            aria-label="Select language"
          >
            <span className="text-lg">{currentLanguage?.flag}</span>
            <span className="text-sm font-medium text-gray-700 dark:text-[#FFFFFF]">{currentLanguage?.name}</span>
            <svg 
              className={`w-4 h-4 text-gray-500 dark:text-[#FF9800] transition-transform duration-200 ${isLanguageOpen ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isLanguageOpen && (
            <>
              <div 
                className="fixed inset-0 z-40" 
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) {
                    setIsLanguageOpen(false);
                  }
                }}
              />
              <div 
                className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#1A1A1A] border border-indigo-200 dark:border-[#333333] rounded-lg shadow-lg z-50"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div className="py-1">
                  {availableLanguages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        playNavigationClick();
                        handleLanguageChange(lang.code);
                        // Track language change
                        if (typeof gtag !== 'undefined') {
                          gtag('event', 'preference_changed', {
                            'preference_type': 'language',
                            'preference_value': lang.code
                          });
                        }
                      }}
              
                      className={`w-full flex items-center space-x-3 px-4 py-2 text-sm hover:bg-indigo-50 dark:hover:bg-[#1E1E1E] transition-colors duration-200 ${
                        language === lang.code ? 'bg-indigo-50 dark:bg-[#1E1E1E] text-indigo-700 dark:text-[#FF9800]' : 'text-gray-700 dark:text-[#B0B0B0]'
                      }`}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <span className="font-medium">
                        {lang.name}
                      </span>
                      {language === lang.code && (
                        <svg className="w-4 h-4 text-indigo-600 dark:text-[#FF5722] ml-auto" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default NavigationMenu;

